import {
  MANUSCRIPT_CONTENT_VERSION,
  manuscriptContentFromPlainText,
  normalizeManuscriptContent,
  plainTextFromManuscriptContent,
} from "./childrensBookManuscriptContent";


export const MANUSCRIPT_IMPORT_APPLICATION_DECISIONS =
  Object.freeze({
    SKIP:
      "skip",

    ADD:
      "add",

    APPEND:
      "append",

    REPLACE:
      "replace",
  });


function clean(
  value
) {
  return String(
    value || ""
  ).trim();
}


function manuscriptText(
  value
) {
  return String(
    value || ""
  ).trim();
}


export function countManuscriptImportWords(
  value
) {
  const text =
    manuscriptText(
      value
    );

  return text
    ? text.split(
        /\s+/
      ).length
    : 0;
}


function safeObject(
  value
) {
  return (
    value &&
    typeof value ===
      "object" &&
    !Array.isArray(
      value
    )
  )
    ? value
    : {};
}


function hasOwn(
  value,
  key
) {
  return Object.prototype
    .hasOwnProperty.call(
      safeObject(
        value
      ),
      key
    );
}


function cloneEntry(
  entry = {}
) {
  return {
    ...safeObject(
      entry
    ),
  };
}


function cloneManuscriptPlan(
  manuscriptPlan = {}
) {
  const source =
    safeObject(
      manuscriptPlan
    );

  return {
    ...source,

    entries:
      (
        Array.isArray(
          source.entries
        )
          ? source.entries
          : []
      ).map(
        cloneEntry
      ),
  };
}


function firstLegacyText(
  value,
  keys
) {
  const source =
    safeObject(
      value
    );

  for (
    const key of keys
  ) {
    const text =
      manuscriptText(
        source[key]
      );

    if (text) {
      return text;
    }
  }

  return "";
}


function resolvedManuscriptSource(
  value,
  {
    legacyKeys = [],
    invalidCode,
    sectionId = "",
    unitId = "",
  } = {}
) {
  const source =
    safeObject(
      value
    );

  if (
    hasOwn(
      source,
      "manuscriptContent"
    )
  ) {
    const normalized =
      normalizeManuscriptContent(
        source.manuscriptContent
      );

    if (
      !normalized.ok ||
      !normalized.content
    ) {
      return {
        ok: false,

        error:
          applicationError(
            invalidCode,
            {
              sectionId,
              unitId,
            }
          ),
      };
    }

    return {
      ok: true,

      content:
        normalized.content,

      text:
        plainTextFromManuscriptContent(
          normalized.content
        ),

      hasContent:
        Boolean(
          manuscriptText(
            plainTextFromManuscriptContent(
              normalized.content
            )
          )
        ),

      structured:
        true,
    };
  }

  const text =
    firstLegacyText(
      source,
      legacyKeys
    );

  const normalized =
    normalizeManuscriptContent(
      manuscriptContentFromPlainText(
        text
      )
    );

  return {
    ok:
      normalized.ok,

    content:
      normalized.content,

    text,

    hasContent:
      Boolean(text),

    structured:
      false,

    error:
      normalized.ok
        ? null
        : applicationError(
            invalidCode,
            {
              sectionId,
              unitId,
            }
          ),
  };
}


function appendedManuscriptContent(
  currentContent,
  importedContent
) {
  return normalizeManuscriptContent({
    version:
      MANUSCRIPT_CONTENT_VERSION,

    blocks: [
      ...(
        Array.isArray(
          currentContent
            ?.blocks
        )
          ? currentContent
              .blocks
          : []
      ),

      ...(
        Array.isArray(
          importedContent
            ?.blocks
        )
          ? importedContent
              .blocks
          : []
      ),
    ],
  });
}


function applicationError(
  code,
  {
    sectionId = "",
    unitId = "",
  } = {}
) {
  return {
    code,

    sectionId:
      clean(
        sectionId
      ),

    unitId:
      clean(
        unitId
      ),
  };
}


function orderedEntries(
  originalEntries,
  nextByUnit,
  bookMapUnits
) {
  const ordered = [];
  const included =
    new Set();

  originalEntries.forEach(
    (entry) => {
      const unitId =
        clean(
          entry?.unitId
        );

      if (
        !unitId ||
        included.has(
          unitId
        ) ||
        !nextByUnit.has(
          unitId
        )
      ) {
        return;
      }

      ordered.push(
        cloneEntry(
          nextByUnit.get(
            unitId
          )
        )
      );

      included.add(
        unitId
      );
    }
  );

  bookMapUnits.forEach(
    (unit) => {
      const unitId =
        clean(
          unit?.id
        );

      if (
        !unitId ||
        included.has(
          unitId
        ) ||
        !nextByUnit.has(
          unitId
        )
      ) {
        return;
      }

      ordered.push(
        cloneEntry(
          nextByUnit.get(
            unitId
          )
        )
      );

      included.add(
        unitId
      );
    }
  );

  nextByUnit.forEach(
    (
      entry,
      unitId
    ) => {
      if (
        included.has(
          unitId
        )
      ) {
        return;
      }

      ordered.push(
        cloneEntry(
          entry
        )
      );

      included.add(
        unitId
      );
    }
  );

  return ordered;
}


export function prepareChildrensBookManuscriptImport({
  sections = [],
  bookMapUnits = [],
  manuscriptPlan = {},
  mappingBySection = {},
  decisionBySection = {},
} = {}) {
  const sourceSections =
    Array.isArray(
      sections
    )
      ? sections
      : [];

  const units =
    Array.isArray(
      bookMapUnits
    )
      ? bookMapUnits
      : [];

  const mappings =
    safeObject(
      mappingBySection
    );

  const decisions =
    safeObject(
      decisionBySection
    );

  const previousManuscriptPlan =
    cloneManuscriptPlan(
      manuscriptPlan
    );

  const originalEntries =
    previousManuscriptPlan
      .entries;

  const errors = [];
  const sectionIds =
    new Set();

  const unitsById =
    new Map();

  const entriesByUnit =
    new Map();

  const destinationOwners =
    new Map();

  units.forEach(
    (unit) => {
      const unitId =
        clean(
          unit?.id
        );

      if (!unitId) {
        return;
      }

      if (
        unitsById.has(
          unitId
        )
      ) {
        errors.push(
          applicationError(
            "duplicate_book_map_unit",
            {
              unitId,
            }
          )
        );

        return;
      }

      unitsById.set(
        unitId,
        unit
      );
    }
  );

  originalEntries.forEach(
    (entry) => {
      const unitId =
        clean(
          entry?.unitId
        );

      if (!unitId) {
        errors.push(
          applicationError(
            "invalid_manuscript_entry"
          )
        );

        return;
      }

      if (
        entriesByUnit.has(
          unitId
        )
      ) {
        errors.push(
          applicationError(
            "duplicate_manuscript_entry",
            {
              unitId,
            }
          )
        );

        return;
      }

      entriesByUnit.set(
        unitId,
        cloneEntry(
          entry
        )
      );
    }
  );

  const operations = [];

  sourceSections.forEach(
    (section) => {
      const sectionId =
        clean(
          section?.id
        );

      if (!sectionId) {
        errors.push(
          applicationError(
            "missing_section_identifier"
          )
        );

        return;
      }

      if (
        sectionIds.has(
          sectionId
        )
      ) {
        errors.push(
          applicationError(
            "duplicate_section",
            {
              sectionId,
            }
          )
        );

        return;
      }

      sectionIds.add(
        sectionId
      );

      const imported =
        resolvedManuscriptSource(
          section,
          {
            legacyKeys: [
              "text",
              "manuscriptText",
              "excerpt",
            ],

            invalidCode:
              "invalid_imported_manuscript_content",

            sectionId,
          }
        );

      if (!imported.ok) {
        errors.push(
          imported.error
        );

        return;
      }

      const decision =
        clean(
          decisions[
            sectionId
          ]
        );

      if (!decision) {
        errors.push(
          applicationError(
            "unresolved_section",
            {
              sectionId,
            }
          )
        );

        return;
      }

      if (
        decision ===
        MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
          .SKIP
      ) {
        operations.push({
          sectionId,
          unitId: "",
          decision,

          importedContent:
            imported.content,

          importedText:
            imported.text,

          currentContent:
            null,

          currentText: "",

          nextContent:
            null,

          nextText: "",

          importedWordCount:
            countManuscriptImportWords(
              imported.text
            ),

          currentWordCount: 0,
          resultingWordCount: 0,
        });

        return;
      }

      const supported = [
        MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
          .ADD,

        MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
          .APPEND,

        MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
          .REPLACE,
      ];

      if (
        !supported.includes(
          decision
        )
      ) {
        errors.push(
          applicationError(
            "unsupported_decision",
            {
              sectionId,
            }
          )
        );

        return;
      }

      const unitId =
        clean(
          mappings[
            sectionId
          ]
        );

      if (!unitId) {
        errors.push(
          applicationError(
            "missing_destination",
            {
              sectionId,
            }
          )
        );

        return;
      }

      if (
        !unitsById.has(
          unitId
        )
      ) {
        errors.push(
          applicationError(
            "destination_not_found",
            {
              sectionId,
              unitId,
            }
          )
        );

        return;
      }

      const existingOwner =
        destinationOwners.get(
          unitId
        );

      if (
        existingOwner &&
        existingOwner !==
          sectionId
      ) {
        errors.push(
          applicationError(
            "duplicate_destination",
            {
              sectionId,
              unitId,
            }
          )
        );

        return;
      }

      destinationOwners.set(
        unitId,
        sectionId
      );

      if (
        !imported.hasContent
      ) {
        errors.push(
          applicationError(
            "missing_imported_text",
            {
              sectionId,
              unitId,
            }
          )
        );

        return;
      }

      const currentEntry =
        entriesByUnit.get(
          unitId
        ) || null;

      const current =
        resolvedManuscriptSource(
          currentEntry,
          {
            legacyKeys: [
              "manuscriptText",
            ],

            invalidCode:
              "invalid_existing_manuscript_content",

            sectionId,
            unitId,
          }
        );

      if (!current.ok) {
        errors.push(
          current.error
        );

        return;
      }

      if (
        decision ===
          MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
            .ADD &&
        current.hasContent
      ) {
        errors.push(
          applicationError(
            "add_requires_empty_destination",
            {
              sectionId,
              unitId,
            }
          )
        );

        return;
      }

      if (
        (
          decision ===
            MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
              .APPEND ||
          decision ===
            MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
              .REPLACE
        ) &&
        !current.hasContent
      ) {
        errors.push(
          applicationError(
            "existing_text_required",
            {
              sectionId,
              unitId,
            }
          )
        );

        return;
      }

      let nextContent =
        imported.content;

      if (
        decision ===
        MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
          .APPEND
      ) {
        const appended =
          appendedManuscriptContent(
            current.content,
            imported.content
          );

        if (
          !appended.ok ||
          !appended.content
        ) {
          errors.push(
            applicationError(
              "combined_manuscript_content_invalid",
              {
                sectionId,
                unitId,
              }
            )
          );

          return;
        }

        nextContent =
          appended.content;
      }

      const nextText =
        plainTextFromManuscriptContent(
          nextContent
        );

      operations.push({
        sectionId,
        unitId,
        decision,

        importedContent:
          imported.content,

        importedText:
          imported.text,

        currentContent:
          current.content,

        currentText:
          current.text,

        nextContent,
        nextText,

        importedWordCount:
          countManuscriptImportWords(
            imported.text
          ),

        currentWordCount:
          countManuscriptImportWords(
            current.text
          ),

        resultingWordCount:
          countManuscriptImportWords(
            nextText
          ),
      });
    }
  );

  if (
    errors.length > 0
  ) {
    return {
      ok: false,
      errors,
      plan: null,
    };
  }

  const nextByUnit =
    new Map(
      Array.from(
        entriesByUnit.entries()
      ).map(
        ([
          unitId,
          entry,
        ]) => [
          unitId,
          cloneEntry(
            entry
          ),
        ]
      )
    );

  operations.forEach(
    (operation) => {
      if (
        operation.decision ===
        MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
          .SKIP
      ) {
        return;
      }

      const current =
        nextByUnit.get(
          operation.unitId
        );

      const nextEntry =
        current
          ? {
              ...current,

              manuscriptContent:
                operation.nextContent,

              manuscriptText:
                operation.nextText,
            }
          : {
              unitId:
                operation.unitId,

              manuscriptContent:
                operation.nextContent,

              manuscriptText:
                operation.nextText,

              authorNote:
                "",

              status:
                "drafting",
            };

      nextByUnit.set(
        operation.unitId,
        nextEntry
      );
    }
  );

  const nextEntries =
    orderedEntries(
      originalEntries,
      nextByUnit,
      units
    );

  const summary =
    operations.reduce(
      (
        result,
        operation
      ) => {
        if (
          operation.decision ===
          MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
            .SKIP
        ) {
          result.skipped += 1;
          return result;
        }

        result.imported += 1;

        result.importedWords +=
          operation
            .importedWordCount;

        result.affectedUnitIds.push(
          operation.unitId
        );

        if (
          operation.decision ===
          MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
            .ADD
        ) {
          result.added += 1;
        }

        if (
          operation.decision ===
          MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
            .APPEND
        ) {
          result.appended += 1;
        }

        if (
          operation.decision ===
          MANUSCRIPT_IMPORT_APPLICATION_DECISIONS
            .REPLACE
        ) {
          result.replaced += 1;

          result.removedWords +=
            operation
              .currentWordCount;
        }

        return result;
      },
      {
        imported: 0,
        skipped: 0,
        added: 0,
        appended: 0,
        replaced: 0,
        importedWords: 0,
        removedWords: 0,
        affectedUnitIds: [],
      }
    );

  const nextManuscriptPlan = {
    ...previousManuscriptPlan,

    entries:
      nextEntries,
  };

  return {
    ok: true,
    errors: [],
    plan: {
      operations,
      summary,

      requiresReplacementConfirmation:
        summary.replaced > 0,

      previousManuscriptPlan,
      nextManuscriptPlan,
    },
  };
}
